import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import * as listCache from '../../../../services/listCache';
import DatePicker from '../../../Reusable_Components/Template_Components/DatePicker';
import AsyncSelect from '../../../Reusable_Components/Template_Components/AsyncSelect';
import AsyncMultiSelect from '../../../Reusable_Components/Template_Components/AsyncMultiSelect';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'New',            label: 'New',             color: '#3b82f6' },
  { value: 'Contacted',      label: 'Contacted',       color: '#8b5cf6' },
  { value: 'Qualifying',     label: 'Qualifying',      color: '#f59e0b' },
  { value: 'Qualified',      label: 'Qualified',       color: '#22c55e' },
  { value: 'Proposal Sent',  label: 'Proposal Sent',   color: '#0ea5e9' },
  { value: 'Negotiating',    label: 'Negotiating',     color: '#e87860' },
  { value: 'Converted',      label: 'Converted',       color: '#16a34a' },
  { value: 'Unqualified',    label: 'Unqualified',     color: '#94a3b8' },
  { value: 'Lost',           label: 'Lost',            color: '#ef4444' },
];

const SOURCE_OPTIONS = [
  'Website', 'Referral', 'Cold Call', 'Email Campaign',
  'LinkedIn', 'Facebook / Instagram', 'Trade Show / Event',
  'Partner / Reseller', 'Walk-in', 'Other',
];

const PRIORITY = {
  Low:    { bg: '#f0fdf4', fg: '#15803d', activeBg: '#dcfce7', border: '#bbf7d0' },
  Medium: { bg: '#fffbeb', fg: '#a16207', activeBg: '#fef3c7', border: '#fde68a' },
  High:   { bg: '#fef2f2', fg: '#b91c1c', activeBg: '#fee2e2', border: '#fecaca' },
};

const scoreColor = (v) => {
  if (v >= 80) return '#16a34a';
  if (v >= 60) return '#3b82f6';
  if (v >= 35) return '#f59e0b';
  return '#ef4444';
};

const EMPTY = {
  contact: '',
  lead_source: '',
  status: 'New',
  lead_score: 50,
  assigned_to: [],
  title: '',
  company: '',
  email: '',
  phone: '',
  priority: 'Medium',
  expected_value: '',
  expected_close_date: '',
  description: '',
  follow_up_date: '',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Field = ({ label, required, children, wide }) => (
  <div className={`form-field${wide ? ' span-full' : ''}`}>
    <label className="form-label">{label}{required && <span className="req"> *</span>}</label>
    {children}
  </div>
);

const PrioritySelector = ({ value, onChange }) => (
  <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
    {['Low', 'Medium', 'High'].map((p, i) => {
      const active = value === p;
      const c = PRIORITY[p];
      return (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          style={{
            flex: 1, padding: '8px 0', fontSize: 12, fontWeight: 600,
            border: 'none', borderRight: i < 2 ? '1px solid var(--border)' : 'none',
            cursor: 'pointer', transition: 'all 0.13s',
            background: active ? c.activeBg : '#fff',
            color: active ? c.fg : 'var(--text-muted)',
          }}
        >
          {p}
        </button>
      );
    })}
  </div>
);

const StatusDot = ({ color }) => (
  <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', marginRight: 6 }} />
);

const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 20, paddingBottom: 14,
    borderBottom: '1px solid var(--border)',
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 8,
      background: 'var(--color-primary-pale)', color: 'var(--color-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{subtitle}</div>}
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const LeadInput = () => {
  const { leadId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const decodedId = leadId ? atob(leadId) : '';
  const id = decodedId.replace(/\D/g, '');
  const isEditing = !!id;

  const [form, setForm] = useState(EMPTY);
  const [original, setOriginal] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leadId) return;
    axios.get(`${API_DOMAIN}${API_ROUTES.lead}${id}/`, { headers })
      .then(res => {
        const data = res.data;
        const contactId = data.contact && typeof data.contact === 'object' ? data.contact.id : data.contact;
        setForm(prev => ({ ...prev, ...data, contact: contactId || '' }));
        setOriginal(data);
      })
      .catch(err => console.error('Lead fetch error:', err));
  }, [leadId, id]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = isEditing
        ? `${API_DOMAIN}${API_ROUTES.lead}${id}/`
        : `${API_DOMAIN}${API_ROUTES.lead}`;
      const method = isEditing ? axios.put : axios.post;
      // DRF's DateField/DecimalField reject '' outright ("wrong format") — an
      // empty optional field must be sent as null, not the input's raw ''.
      const payload = {
        ...form,
        expected_close_date: form.expected_close_date || null,
        follow_up_date: form.follow_up_date || null,
        expected_value: form.expected_value === '' ? null : form.expected_value,
      };
      await method(url, payload, { headers });
      listCache.invalidate((u) => u.startsWith(`${API_DOMAIN}${API_ROUTES.lead}`));
      toast?.addToast({ type: 'success', message: isEditing ? 'Lead updated successfully.' : 'Lead created.' });
      navigate('/lead/list');
    } catch (err) {
      const data = err?.response?.data;
      const msg = data
        ? typeof data === 'string' ? data
          : Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ')
        : 'Failed to save. Please check your inputs and try again.';
      toast?.addToast({ type: 'error', message: msg, duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const score = Number(form.lead_score) || 0;
  const currentStatus = STATUS_OPTIONS.find(s => s.value === form.status) || STATUS_OPTIONS[0];

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
            <Link to="/lead/list" style={{ color: 'var(--color-primary)' }}>Leads</Link>
            <span>›</span>
            <span>{isEditing ? 'Edit Lead' : 'New Lead'}</span>
          </div>
          <h1 className="page-title">{isEditing ? 'Edit Lead Details' : 'Create New Lead'}</h1>
          <p className="page-subtitle">Fill in the details below to {isEditing ? 'update this' : 'add a new'} lead to the pipeline</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/lead/list')}>
            Cancel
          </button>
        </div>
      </div>

      <form id="lead-form" onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>

          {/* ── Left column — main form ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Section 1: Lead Overview */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="🎯" title="Lead Overview" subtitle="Basic information about this lead" />
                <div className="form-grid">
                  <Field label="Lead Title / Subject" required wide>
                    <input
                      className="form-input"
                      placeholder="e.g. Enterprise SaaS Subscription — Acme Corp"
                      value={form.title}
                      onChange={set('title')}
                      required
                    />
                  </Field>
                  <Field label="Status" required>
                    <select className="form-select" value={form.status} onChange={set('status')} required>
                      {STATUS_OPTIONS.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Priority">
                    <PrioritySelector value={form.priority} onChange={val => setForm(p => ({ ...p, priority: val }))} />
                  </Field>
                </div>
              </div>
            </div>

            {/* Section 2: Contact Information */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="👤" title="Contact Information" subtitle="Who is this lead associated with?" />
                <div className="form-grid">
                  <Field label="Linked Contact">
                    <AsyncSelect
                      label=""
                      endpoint={API_ROUTES.contact}
                      getOptionLabel={(c) => c.name || c.first_name + ' ' + (c.last_name || '')}
                      value={form.contact}
                      onChange={set('contact')}
                      placeholder="Search contacts…"
                    />
                  </Field>
                  <Field label="Company / Organization">
                    <input
                      className="form-input"
                      placeholder="e.g. Acme Corporation"
                      value={form.company}
                      onChange={set('company')}
                    />
                  </Field>
                  <Field label="Email Address">
                    <input
                      className="form-input"
                      type="email"
                      placeholder="contact@example.com"
                      value={form.email}
                      onChange={set('email')}
                    />
                  </Field>
                  <Field label="Phone Number">
                    <input
                      className="form-input"
                      type="tel"
                      placeholder="+254 700 000 000"
                      value={form.phone}
                      onChange={set('phone')}
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* Section 3: Pipeline Details */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="📊" title="Pipeline Details" subtitle="Track the lead's progress and financial potential" />
                <div className="form-grid">
                  <Field label="Lead Source">
                    <select className="form-select" value={form.lead_source} onChange={set('lead_source')}>
                      <option value="">— Select source —</option>
                      {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Expected Value (KES)">
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="1000"
                      placeholder="0"
                      value={form.expected_value}
                      onChange={set('expected_value')}
                    />
                  </Field>
                  <Field label="Expected Close Date">
                    <DatePicker
                      value={form.expected_close_date}
                      onChange={set('expected_close_date')}
                      placeholder="Select close date"
                      dateConstraint="future"
                    />
                  </Field>
                  <Field label="Follow-up Date">
                    <DatePicker
                      value={form.follow_up_date}
                      onChange={set('follow_up_date')}
                      placeholder="Select follow-up date"
                      dateConstraint="future"
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* Section 4: Assignment & Notes */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="👥" title="Assignment &amp; Notes" subtitle="Who is handling this lead, and any context" />
                <div className="form-grid">
                  <div className="form-field span-full">
                    <AsyncMultiSelect
                      label="Assigned To"
                      endpoint={API_ROUTES.users}
                      getOptionLabel={(u) => u.username || u.email || `User ${u.id}`}
                      value={form.assigned_to || []}
                      onChange={set('assigned_to')}
                    />
                  </div>
                  <Field label="Description / Notes" wide>
                    <textarea
                      className="form-textarea"
                      rows={4}
                      placeholder="Add context, conversation history, or any relevant notes about this lead…"
                      value={form.description}
                      onChange={set('description')}
                    />
                  </Field>
                </div>
              </div>
            </div>

          </div>

          {/* ── Right column — sidebar ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Status badge */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Lead Status</span>
              </div>
              <div className="card-body">
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 16px', borderRadius: 8,
                  background: currentStatus.color + '15',
                  border: `1px solid ${currentStatus.color}40`,
                }}>
                  <StatusDot color={currentStatus.color} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: currentStatus.color }}>
                    {currentStatus.label}
                  </span>
                </div>
                {form.priority && (
                  <div style={{
                    marginTop: 10, display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 12, color: 'var(--text-sub)',
                  }}>
                    Priority:
                    <span style={{
                      background: PRIORITY[form.priority]?.activeBg,
                      color: PRIORITY[form.priority]?.fg,
                      padding: '2px 10px', borderRadius: 99, fontWeight: 600, fontSize: 11,
                    }}>
                      {form.priority}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Lead score */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Lead Score</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: scoreColor(score), fontVariantNumeric: 'tabular-nums' }}>
                  {score}
                </span>
              </div>
              <div className="card-body">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={score}
                  onChange={e => setForm(p => ({ ...p, lead_score: e.target.value }))}
                  style={{ width: '100%', accentColor: scoreColor(score) }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  <span>Cold</span>
                  <span>Warm</span>
                  <span>Hot 🔥</span>
                </div>
                <div style={{
                  marginTop: 12, height: 6, borderRadius: 99,
                  background: 'var(--border)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', borderRadius: 99,
                    width: `${score}%`,
                    background: scoreColor(score),
                    transition: 'width 0.2s, background 0.2s',
                  }} />
                </div>
                <div style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: scoreColor(score), fontWeight: 600 }}>
                  {score >= 80 ? 'Hot lead — prioritise!' : score >= 60 ? 'Warm — follow up soon' : score >= 35 ? 'Needs nurturing' : 'Cold — early stage'}
                </div>
              </div>
            </div>

            {/* Quick tips */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Quick Tips</span>
              </div>
              <div className="card-body">
                {[
                  { icon: '📧', text: 'Log an email after saving to track communication' },
                  { icon: '📅', text: 'Set a follow-up date to stay on top of this lead' },
                  { icon: '💰', text: 'Add an expected value to help forecast revenue' },
                ].map(({ icon, text }, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-sub)',
                    marginBottom: i < 2 ? 10 : 0,
                  }}>
                    <span>{icon}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* ── Form actions ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
          <button type="button" className="btn btn-danger" onClick={() => setForm(isEditing ? original : EMPTY)}>
            Reset
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ minWidth: 140 }}>
            {saving
              ? <><span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /> Saving…</>
              : isEditing ? '✓ Update Lead' : '+ Create Lead'
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadInput;
